import { useState, useRef, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { getMediaUrl, useGetLesson, useGetLessons, useGetSpace, useUpdateProgress } from '@onesource/services';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { MdPictureAsPdf, MdDownload } from 'react-icons/md';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

// Set PDF.js worker for react-pdf v10+ (using CDN for compatibility with Vite)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDF Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/pdf/:contentId
 * Shows a PDF content block with viewer/download
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/pdf/$contentId',
)({
  component: PdfContentRoute,
});

function PdfContentRoute() {
  const { id: spaceId, lessonId, contentId, moduleId } = Route.useParams();

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  const [pdfLoading, setPdfLoading] = useState<boolean>(true);
  const [useFallback, setUseFallback] = useState<boolean>(false);

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Track PDF view on mount via progress update
  const trackedRef = useRef(false);
  const { mutate: updateProgress } = useUpdateProgress(lessonId);
  const pdfBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.PDF,
  );
  useEffect(() => {
    if (!trackedRef.current && pdfBlock) {
      trackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: contentId });
    }
  }, [contentId, pdfBlock, updateProgress]);

  // PDF navigation and zoom handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  // Fallback to iframe if react-pdf fails (e.g., CORS issues with external PDFs)
  const onDocumentLoadError = () => {
    setPdfLoading(false);
    setUseFallback(true);
  };

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 2.5));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!pdfBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">PDF not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Document"
      />

      {/* PDF Viewer/Download Section */}
      <Box>
        {pdfBlock.pdfUrl ? (
          <Box display="flex" gap={3}>
            {/* PDF Controls - only show for react-pdf viewer */}
            {!useFallback && !pdfLoading && (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 2,
                  p: 2,
                  backgroundColor: 'grey.100',
                  borderRadius: 2,
                  maxWidth: 900,
                }}
              >
                <IconButton onClick={goToPrevPage} disabled={pageNumber <= 1}>
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 120, textAlign: 'center' }}>
                  Page {pageNumber} of {numPages || pdfBlock.pdfPageCount || '?'}
                </Typography>
                <IconButton onClick={goToNextPage} disabled={pageNumber >= numPages}>
                  <NavigateNextIcon />
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
                  {Math.round(scale * 100)}%
                </Typography>
                <IconButton onClick={zoomIn} disabled={scale >= 2.5}>
                  <ZoomInIcon />
                </IconButton>
              </Box>
            )}

            {/* PDF Viewer - react-pdf or iframe fallback */}
            {useFallback ? (
              // Fallback to iframe for external PDFs with CORS issues
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  width: '100%',
                  maxWidth: 900,
                  height: 750,
                  borderRadius: '15px',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <iframe
                  src={`${getMediaUrl(pdfBlock.pdfUrl)}#view=FitH`}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title={pdfBlock.title || 'PDF Viewer'}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: 'calc(100vh - 400px)',
                  minHeight: 750,
                  width: '100%',
                  maxWidth: 900,
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'center',
                  backgroundColor: 'grey.200',
                }}
              >
                {pdfLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                )}
                <Document
                  file={pdfBlock.pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={null}
                >
                  <Page pageNumber={pageNumber} scale={scale} renderTextLayer renderAnnotationLayer />
                </Document>
              </Box>
            )}

            {/* Download Card */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column' },
                gap: { xs: 2, md: 3 },
                p: { xs: 3, md: 3.5 },
                bgcolor: 'white',
                borderRadius: '15px',
                height: 'fit-content',
                maxWidth: { xs: '100%', md: 235 },
                mb: 3,
              }}
            >
              {pdfBlock.pdfBannerUrl && getMediaUrl(pdfBlock.pdfBannerUrl) ? (
                <Box
                  component="img"
                  src={getMediaUrl(pdfBlock.pdfBannerUrl)}
                  alt={pdfBlock.title || 'Document'}
                  sx={{
                    width: { xs: '66px', md: '90px' },
                    height: { xs: '81px', md: '109px' },
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <MdPictureAsPdf size={48} color="#d32f2f" />
              )}{' '}
              <Box
                sx={{
                  gap: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: { xs: '12px', md: '14px' } }}>
                  {pdfBlock.pdfFilename || 'Document.pdf'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<MdDownload />}
                  href={getMediaUrl(pdfBlock.pdfUrl) || ''}
                  target="_blank"
                  size="small"
                  rel="noopener noreferrer"
                >
                  Download
                </Button>
                {pdfBlock.pdfPageCount && (
                  <Typography variant="caption" color="text.secondary">
                    {pdfBlock.pdfPageCount} pages
                    {pdfBlock.pdfFileSize && ` • ${Math.round(pdfBlock.pdfFileSize / 1024)} KB`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: 400,
              bgcolor: 'grey.100',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              maxWidth: 900,
            }}
          >
            <MdPictureAsPdf size={64} color="#999" />
            <Typography color="text.secondary">PDF not available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
